import { Link } from "react-router-dom";
import { Award, ShieldCheck, CheckCircle2, ExternalLink, FileCheck, Zap } from "lucide-react";

const CERTS = [
  {
    id: "qualipac",
    name: "QualiPAC",
    tagline: "Installation de pompes à chaleur — RGE Qualit'EnR",
    color: "from-[#0055A4] to-[#003D7A]",
    accent: "#0055A4",
    scope: "Chauffage & Eau chaude sanitaire",
    emblem: "QP",
    delivered: "Qualit'EnR",
    year: "2025",
    description:
      "La qualification QualiPAC atteste de notre expertise dans l'installation, la mise en service et la maintenance des pompes à chaleur aérothermiques et géothermiques. Elle est délivrée par Qualit'EnR et reconnue par l'État français dans le cadre du dispositif RGE.",
    benefits: [
      "Éligibilité MaPrimeRénov' et CEE pour vos clients",
      "Contrôles qualité annuels sur chantiers",
      "Formation continue des équipes techniques",
      "Assurance décennale conforme",
    ],
    reference: "Référence : QualiPAC Chauffage & ECS 2025",
    site: "https://www.qualit-enr.org",
  },
  {
    id: "qualibat-rge",
    name: "Qualibat RGE",
    tagline: "Reconnu Garant de l'Environnement",
    color: "from-[#10B981] to-[#059669]",
    accent: "#10B981",
    scope: "Rénovation énergétique globale",
    emblem: "RGE",
    delivered: "Qualibat",
    year: "2025",
    description:
      "La qualification Qualibat RGE certifie notre expertise en rénovation énergétique. Délivrée par Qualibat, elle garantit à nos clients que les travaux réalisés respectent les critères de performance énergétique exigés pour bénéficier des aides publiques.",
    benefits: [
      "Signe de confiance officiel reconnu par l'État",
      "Audit régulier des compétences techniques",
      "Respect strict des normes DTU en vigueur",
      "Traçabilité complète des chantiers",
    ],
    reference: "Référence : Qualibat RGE Rénovation énergétique",
    site: "https://www.qualibat.com",
  },
];

export default function Certifications() {
  return (
    <main className="min-h-screen bg-white text-slate-900" data-testid="page-certifications">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0F172A] via-[#0B1220] to-[#0F172A] text-white pt-40 pb-24">
        <div className="pointer-events-none absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#10B981]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#0055A4]/10 blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/25 bg-[#10B981]/[0.06] px-3 py-1.5 mb-6">
            <Award className="h-3.5 w-3.5 text-[#10B981]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#10B981] font-bold">Nos certifications</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Un savoir-faire certifié <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#34D399]">par l&apos;État français.</span>
          </h1>
          <p className="mt-6 text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            ECONATION-CEE et son réseau d&apos;artisans partenaires détiennent les qualifications officielles
            requises pour vous garantir des travaux de qualité et l&apos;éligibilité aux aides publiques.
          </p>
        </div>
      </section>

      {/* Certifications detail */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 space-y-16">
          {CERTS.map((cert, idx) => (
            <article
              key={cert.id}
              data-testid={`cert-${cert.id}`}
              className="grid lg:grid-cols-[280px_1fr] gap-8 lg:gap-12 items-start"
            >
              {/* Logo card */}
              <div className={idx % 2 === 0 ? "lg:order-1" : "lg:order-2"}>
                <div className="relative">
                  <div
                    className="absolute -inset-4 rounded-3xl opacity-25 blur-2xl"
                    style={{ background: `linear-gradient(135deg, ${cert.accent}, transparent)` }}
                  />
                  <div className="relative rounded-2xl bg-white border-2 border-slate-200 p-8 shadow-sm text-center overflow-hidden">
                    {/* Emblem */}
                    <div
                      className={`mx-auto mb-4 h-24 w-24 rounded-2xl bg-gradient-to-br ${cert.color} grid place-items-center shadow-lg`}
                    >
                      <span className="text-white font-display text-3xl font-bold tracking-tight">{cert.emblem}</span>
                    </div>
                    <div className="text-slate-900 font-display font-bold text-xl tracking-tight">{cert.name}</div>
                    <div className="mt-2 text-slate-500 text-xs uppercase tracking-[0.18em] font-semibold">
                      Délivré par {cert.delivered}
                    </div>
                    <div className="mt-1 text-slate-400 text-[11px]">Validité {cert.year}</div>
                    {/* Corner badge RGE */}
                    <div
                      className="absolute -top-1 -right-1 rotate-12 px-3 py-1 rounded-bl-xl rounded-tr-2xl text-white text-[10px] font-bold tracking-[0.18em]"
                      style={{ backgroundColor: cert.accent }}
                    >
                      RGE
                    </div>
                  </div>
                </div>
                <div className={`mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${cert.color} text-white px-4 py-2 shadow-md`}>
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-bold tracking-wide">CERTIFIÉ RGE</span>
                </div>
              </div>

              {/* Content */}
              <div className={idx % 2 === 0 ? "lg:order-2" : "lg:order-1"}>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 mb-3">
                  {cert.scope}
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">
                  {cert.name}
                </h2>
                <p className="text-slate-600 text-lg mb-5">{cert.tagline}</p>
                <p className="text-slate-700 leading-relaxed mb-6">{cert.description}</p>

                <div className="grid sm:grid-cols-2 gap-2 mb-6">
                  {cert.benefits.map((b) => (
                    <div key={b} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{b}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3 items-center">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    <FileCheck className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5 text-slate-500" />
                    {cert.reference}
                  </div>
                  <a
                    href={cert.site}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-900 text-sm font-semibold"
                  >
                    Vérifier l&apos;organisme <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Trust footer */}
      <section className="bg-slate-50 py-16 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-3">
                Vos aides sécurisées.
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Toutes les qualifications RGE sont vérifiables sur le site officiel <strong>France Rénov&apos;</strong>. Elles conditionnent
                l&apos;éligibilité à MaPrimeRénov&apos;, aux CEE et à la prime Coup de pouce Chauffage.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <a
                href="https://france-renov.gouv.fr/annuaire-rge"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-900 hover:bg-slate-900 hover:text-white text-slate-900 px-6 py-3 text-sm font-semibold transition"
              >
                Vérifier sur France Rénov&apos; <ExternalLink className="h-4 w-4" />
              </a>
              <Link
                to="/simulateur-pac"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0055A4] hover:bg-[#003D7A] text-white px-6 py-3 text-sm font-semibold shadow-[0_10px_28px_-10px_rgba(0,85,164,0.6)] hover:-translate-y-0.5 transition"
              >
                <Zap className="h-4 w-4" /> Vérifier mes aides
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
